import type { Lang } from "../index";

export interface CustomerSuccessRenewalCallCopy {
  seo: { title: string; description: string };
  eyebrow: string;
  h1: string;
  lede: string;
  problem: { heading: string; body1: string; body2: string };
  scenarios: {
    heading: string;
    items: { tag: string; title: string; body: string }[];
  };
  how: { heading: string; body: string; bullets: string[] };
  proof: { heading: string; bullets: string[] };
  cta: { heading: string; body: string; primary: string; secondary: string };
}

const en: CustomerSuccessRenewalCallCopy = {
  seo: {
    title: "Minuta for customer success — quarterly business reviews and renewal calls",
    description:
      "CSMs run renewal calls and QBRs with a live English transcript and AI-suggested talking points grounded in the account brief. No bot in the call and audio stays on the CSM's Mac.",
  },
  eyebrow: "Use case · Customer success",
  h1: "Renewal call. Five competing priorities. Minuta keeps you on track.",
  lede:
    "Customer success managers run a sequence of renewal conversations every quarter — usage review, expansion talk, executive summary. Minuta surfaces the right talking point in the moment, grounded in the account brief, without a bot joining the call.",
  problem: {
    heading: "The renewal call problem",
    body1:
      "A renewal call is short, structured, and political. The CSM has to land the usage story, surface the at-risk metric, and tee up the expansion conversation — all in 30 minutes, and often with stakeholders the CSM hasn't met before.",
    body2:
      "The classic failure mode is forgetting to mention the integration that's about to ship, or letting a pricing question pull the conversation away from the renewal ask. A live cue grounded in the account brief is the difference between a clean save and a churned ARR line.",
  },
  scenarios: {
    heading: "Where it earns its keep",
    items: [
      {
        tag: "Talking points",
        title: "QBR with mixed stakeholders",
        body: "Three account-side faces, two of them new. Minuta surfaces the talking point that matches the question on the live transcript — pulling from the account brief, the usage report, and the prior call notes.",
      },
      {
        tag: "Pricing pushback",
        title: "\"What's our renewal price?\"",
        body: "Battlecard for renewal pricing loaded as context. Minuta drafts a 2-sentence answer in your tone, anchored to the value delivered this quarter.",
      },
      {
        tag: "Expansion opening",
        title: "\"We're rolling out to a new team\"",
        body: "When the account drops a buying signal, Minuta surfaces the expansion playbook step the CSM should run next, while the conversation is still live.",
      },
    ],
  },
  how: {
    heading: "How it fits a CS workflow",
    body:
      "Open Minuta a minute before the call. Attach the account brief, last quarter's usage report, and the renewal pricing sheet. Run the call as you normally would.",
    bullets: [
      "No bot joins. The customer sees no extra participant. Audio is captured locally on the CSM's Mac.",
      "Live English transcript on the side, synchronized with the call.",
      "Question detected → 1-click \"What should I say?\" → draft in 2-4 seconds, grounded in the account brief.",
      "Post-call summary with TL;DR, action items, and renewal-risk flags. Drop into the CRM.",
    ],
  },
  proof: {
    heading: "Why CSMs pick Minuta over generic notetakers",
    bullets: [
      "Other notetakers give a transcript. Minuta drafts the answer to the renewal question in real time.",
      "Bot-in-the-call is a non-starter for many enterprise customers — Minuta is invisible.",
      "Account brief and battlecards ground every prompt. No hallucinated commitments.",
      "Zero-retention security — audio of a customer's renewal conversation is processed via secure transient APIs and stays on the CSM's Mac.",
    ],
  },
  cta: {
    heading: "Try it before your next renewal",
    body: "7 days with everything unlocked. No credit card. €9/mo after.",
    primary: "Start free trial",
    secondary: "Download for macOS",
  },
};

const pt: CustomerSuccessRenewalCallCopy = {
  seo: {
    title: "Minuta pra customer success — QBR e call de renovação",
    description:
      "CSM roda call de renovação e QBR com transcript em inglês ao vivo e sugestão de talking point fundamentada no brief da conta. Sem bot na call e o áudio fica no computador do CSM.",
  },
  eyebrow: "Caso de uso · Customer success",
  h1: "Call de renovação. Cinco prioridades competindo. O Minuta te mantém na linha.",
  lede:
    "Customer success roda uma sequência de conversas de renovação todo trimestre — review de uso, conversa de expansão, executive summary. O Minuta sugere o talking point certo no momento, fundamentado no brief da conta, sem bot na call.",
  problem: {
    heading: "O problema da call de renovação",
    body1:
      "Call de renovação é curta, estruturada e política. O CSM precisa entregar a história de uso, levantar a métrica em risco e abrir a conversa de expansão — tudo em 30 minutos, e muitas vezes com stakeholder que o CSM não conhecia antes.",
    body2:
      "O modo de falha clássico é esquecer de mencionar a integração que vai entrar, ou deixar uma pergunta de preço puxar a conversa pra longe do pedido de renovação. Uma sugestão ao vivo fundamentada no brief é o que separa um save limpo de uma linha de ARR perdida.",
  },
  scenarios: {
    heading: "Onde paga o investimento",
    items: [
      {
        tag: "Talking point",
        title: "QBR com stakeholders mistos",
        body: "Três caras do lado da conta, duas pessoas novas. O Minuta sugere o talking point que combina com a pergunta no transcript ao vivo — puxando do brief da conta, do report de uso e das notas das calls anteriores.",
      },
      {
        tag: "Pressão em preço",
        title: "\"Qual é o nosso preço de renovação?\"",
        body: "Battlecard de pricing de renovação carregado como contexto. O Minuta sugere uma resposta de 2 frases no seu tom, ancorada no valor entregue no trimestre.",
      },
      {
        tag: "Abertura de expansão",
        title: "\"Vamos rodar isso pra outro time\"",
        body: "Quando a conta dá um sinal de compra, o Minuta sugere o próximo passo do playbook de expansão enquanto a conversa ainda está acontecendo.",
      },
    ],
  },
  how: {
    heading: "Como entra no fluxo de CS",
    body:
      "Abre o Minuta um minuto antes da call. Anexa o brief da conta, o report de uso do trimestre passado e o sheet de pricing de renovação. Roda a call como sempre.",
    bullets: [
      "Nenhum bot entra. O cliente não vê participante a mais. O áudio é capturado local no computador do CSM.",
      "Transcript em inglês ao vivo do lado, sincronizado com a call.",
      "Pergunta detectada → 1 clique no \"O que responder\" → sugestão em 2-4 segundos, fundamentada no brief da conta.",
      "Resumo pós-call com TL;DR, action items e sinais de risco de renovação. Cola no CRM.",
    ],
  },
  proof: {
    heading: "Por que CSM escolhe Minuta em vez de notetaker genérico",
    bullets: [
      "Outros notetaker entregam transcript. O Minuta sugere a resposta pra pergunta de renovação em tempo real.",
      "Bot na call é não-pra muitos clientes enterprise — o Minuta é invisível.",
      "Brief da conta e battlecard fundamentam cada sugestão. Sem promessa alucinada.",
      "Segurança com retenção zero — o áudio da conversa de renovação é processado temporariamente e fica salvo no computador do CSM.",
    ],
  },
  cta: {
    heading: "Testa antes da sua próxima renovação",
    body: "7 dias com tudo liberado. Sem cartão. €9/mês depois.",
    primary: "Começar trial grátis",
    secondary: "Baixar pro macOS",
  },
};

const es: CustomerSuccessRenewalCallCopy = {
  seo: {
    title: "Minuta para customer success — QBR y calls de renovación",
    description:
      "El CSM corre la call de renovación y el QBR con transcript en vivo en inglés y sugerencia de talking point basada en el brief de la cuenta. Sin bot en la call y el audio se queda en la computadora del CSM.",
  },
  eyebrow: "Caso de uso · Customer success",
  h1: "Call de renovación. Cinco prioridades compitiendo. Minuta te mantiene en línea.",
  lede:
    "Customer success corre una secuencia de conversaciones de renovación cada trimestre — review de uso, conversación de expansión, executive summary. Minuta sugiere el talking point correcto en el momento, basado en el brief de la cuenta, sin bot en la call.",
  problem: {
    heading: "El problema de la call de renovación",
    body1:
      "Una call de renovación es corta, estructurada y política. El CSM tiene que entregar la historia de uso, levantar la métrica en riesgo y abrir la conversación de expansión — todo en 30 minutos, y muchas veces con stakeholders que el CSM no conocía antes.",
    body2:
      "El modo de falla clásico es olvidar mencionar la integración que está por entrar, o dejar que una pregunta de precio se lleve la conversación lejos del pedido de renovación. Una sugerencia en vivo basada en el brief es lo que separa un save limpio de una línea de ARR perdida.",
  },
  scenarios: {
    heading: "Dónde paga la inversión",
    items: [
      {
        tag: "Talking point",
        title: "QBR con stakeholders mixtos",
        body: "Tres caras del lado de la cuenta, dos personas nuevas. Minuta sugiere el talking point que coincide con la pregunta en el transcript en vivo — usando el brief de la cuenta, el report de uso y las notas de calls anteriores.",
      },
      {
        tag: "Presión de precio",
        title: "\"¿Cuál es nuestro precio de renovación?\"",
        body: "Battlecard de pricing de renovación cargado como contexto. Minuta sugiere una respuesta de 2 frases en tu tono, anclada al valor entregado este trimestre.",
      },
      {
        tag: "Apertura de expansión",
        title: "\"Vamos a desplegarlo a otro equipo\"",
        body: "Cuando la cuenta da una señal de compra, Minuta sugiere el siguiente paso del playbook de expansión mientras la conversación todavía está en vivo.",
      },
    ],
  },
  how: {
    heading: "Cómo encaja en un flujo de CS",
    body:
      "Abre Minuta un minuto antes de la call. Adjunta el brief de la cuenta, el report de uso del trimestre pasado y el sheet de pricing de renovación. Corre la call como siempre.",
    bullets: [
      "Ningún bot entra. El cliente no ve un participante extra. El audio se captura local en la computadora del CSM.",
      "Transcript en inglés en vivo al lado, sincronizado con la call.",
      "Pregunta detectada → 1 clic en \"Qué responder\" → sugerencia en 2-4 segundos, basada en el brief de la cuenta.",
      "Resumen post-call con TL;DR, action items y señales de riesgo de renovación. Lo pegas en el CRM.",
    ],
  },
  proof: {
    heading: "Por qué un CSM elige Minuta en vez de un notetaker genérico",
    bullets: [
      "Otros notetakers entregan un transcript. Minuta sugiere la respuesta a la pregunta de renovación en tiempo real.",
      "Un bot en la call es un \"no\" para muchos clientes enterprise — Minuta es invisible.",
      "El brief de la cuenta y los battlecards fundamentan cada sugerencia. Sin promesas alucinadas.",
      "Seguridad con retención cero — el audio de la conversación de renovación se procesa de forma transitoria y se queda en la computadora del CSM.",
    ],
  },
  cta: {
    heading: "Pruébalo antes de tu próxima renovación",
    body: "7 días con todo desbloqueado. Sin tarjeta. €9/mes después.",
    primary: "Empezar prueba gratis",
    secondary: "Descargar para macOS",
  },
};

export const customerSuccessRenewalCallCopy: Record<Lang, CustomerSuccessRenewalCallCopy> = { en, pt, es };
